import { useState, useEffect, useRef } from "react";
import { useSWRConfig } from "swr";
import dayjs from "dayjs";
import parse from "html-react-parser";

import {
  Button,
  Grid,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ThemeProvider,
  useTheme
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLineUpDown, faAbacus } from "@fortawesome/pro-solid-svg-icons";

import {
  LoadingButton,
  TextField,
  AnimatedLogo,
  Checkbox,
  Select
} from "@aclymatepackages/atoms";
import { sumTonsCo2e } from "@aclymatepackages/other-helpers";
import { ucFirstLetters } from "@aclymatepackages/formatters";
import { filterByDateRange } from "@aclymatepackages/date-helpers";
import { mergeDarkTheme } from "@aclymatepackages/themes";

import CollapsibleAlert from "../../atoms/notifications/CollapsibleAlert";
import SlideLayout from "../../layouts/SlideLayout";

import {
  useCachedFirebaseCrud,
  useAccountData
} from "../../../helpers/firebase";
import { useCreateTransactions } from "../../../helpers/components/emissions";
import useEmissionsContext from "../../../helpers/contexts/emissions";
import { useFindAccountingBlockingEmissions } from "../../../helpers/utils/emissions";
import { formatBlockerMessage } from "../../../helpers/utils/accountingBlockers";
import {
  buildElectricityMonthSnapshot,
  buildElectricitySnapshotFields
} from "../../../helpers/utils/accountingElectricitySnapshot";
import useAccountingData from "../../../helpers/hooks/accountingData";
import { analyticsTrack } from "../../../helpers/analytics";
import usePlatformLayoutContext from "../../../helpers/contexts/platformLayout";
import usePermissions from "../../../helpers/hooks/usePermissions";

// Bare dayjs(), not dayjs.utc() -- corrected by #2119 after this file
// previously read UTC based on the (wrong) premise that these dates are
// UTC-anchored. monthlyCommuteEmissionsTons/monthlyUtilitiesEmissionsTons/
// separateUtilitiesOfficesDefaultEmissions are exclusively calcs/recurring
// output (buildCompanyAggregatedEmissionsArrays via
// helpers/contexts/emissions.js -- no manual-entry data is ever mixed in
// before this function sees it), which anchors month starts to LOCAL
// midnight (new Date(year, month, 1)). They're computed fresh by whichever
// browser is viewing the dashboard and read by this same function in that
// same session, so construction and read always share one ambient
// timezone -- a bare local read is self-consistent regardless of what that
// timezone actually is. The UTC read this replaces was the real bug: for a
// positive-UTC-offset viewer, local midnight on January 1st parses in UTC
// as December 31st of the previous year, misclassifying which accounting
// year a recurring entry gets materialized into when a year is closed and
// locked -- see #2119 for the full reasoning and empirical verification.
export const filterRecurringEmissionsForYear = (recurringEmissions, year) =>
  recurringEmissions.filter(({ date }) => dayjs(date).year() === year);

const buildYearsInRange = (startYear, endYear) =>
  Array(Math.max(endYear - startYear + 1, 0))
    .fill()
    .map((_, idx) => startYear + idx);

// Package-synthesized recurring/default entries, same list PrimaryViewTable
// and helpers/components/inputs.js already check for elsewhere -- excluded
// here because @aclymatepackages/calcs/recurring's buildEmissionsMonthsArray
// falls back to its own DEFAULT_START_DATE (the same rolling "last January"
// this whole PR exists to stop faking) whenever it's given no startDate,
// which is exactly the case for a company opening this slider for the first
// time. Without this filter, any company with employees/offices configured
// but no real transaction history yet would derive that rolling default
// right back, one level removed, instead of a genuinely unset result.
const DEFAULT_GENERATED_EMISSION_SOURCES = ["default-utilities", "recurring"];

// Onboarding never asks for a company's accounting start year anymore --
// it's only ever set here, the first time someone tries to set a baseline,
// derived from their earliest real emissions month rather than guessed
// before any data existed. Noon-anchored per EditStartDateButton's own
// convention (a Jan-1-midnight value shifts to Dec 31 of the prior year
// when read in a US timezone).
const findEarliestEmissionDate = (allEmissions) =>
  allEmissions
    .filter(
      ({ source }) => !DEFAULT_GENERATED_EMISSION_SOURCES.includes(source)
    )
    .reduce(
      (earliest, { date }) =>
        earliest && dayjs(earliest).isBefore(date) ? earliest : date,
      null
    );

const deriveStartDateFromEmissions = (allEmissions) => {
  const earliestEmissionDate = findEarliestEmissionDate(allEmissions);

  if (!earliestEmissionDate) {
    return undefined;
  }

  // Bare dayjs(), not dayjs.utc() -- same reasoning as contexts/emissions.js's
  // filterEntriesOutsideClosedYears and this file's own CloseAccountingSlider.
  // The real entries reaching this point (recurring/default-utilities
  // already excluded above) are a mix of transaction/rollup months
  // (UTC-anchored, but with a deliberate +1-day margin specifically so a
  // bare local read is always safe -- see
  // reshapeRollupsV2MonthDocsToLegacyShape) and manually-entered emissions
  // (a real local timestamp, which a bare local read reproduces correctly
  // and a UTC read can misread across a day/month/year boundary). A
  // blanket .utc() read here would be right for the former and wrong for
  // the latter -- an earlier version of this fix did exactly that,
  // reasoning from the transaction/rollup case alone.
  const earliestEmissionYear = dayjs(earliestEmissionDate).year();

  return new Date(earliestEmissionYear, 0, 1, 12);
};

const AccountingChecklistStep = ({ isChecked, title, disabled, onCheck }) => {
  return (
    <Grid item>
      <Checkbox
        label={title}
        value={isChecked}
        disabled={disabled}
        editValue={onCheck}
      />
    </Grid>
  );
};

const AccountingSlideLayoutContent = ({
  actionText,
  setOpen,
  dataLoading,
  accountingYear,
  isBaselineInput,
  startYear,
  lastCompletedYear,
  startDateDerivationFailed,
  hasNoRealStartDate
}) => {
  const {
    monthlyCommuteEmissionsTons,
    monthlyUtilitiesEmissionsTons,
    separateUtilitiesOfficesDefaultEmissions,
    utilitiesTransactions = [],
    allEmissions
  } = useEmissionsContext();

  const findAccountingBlockingEmissions = useFindAccountingBlockingEmissions();

  const [{ unClosedYears = [{}] }] = useAccountingData();
  const { newCollectionDoc, updateAccountData } = useCachedFirebaseCrud();
  const createTransactions = useCreateTransactions();
  const { activateSnackbar } = usePlatformLayoutContext();

  const [saveDataLoading, setSaveDataLoading] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [checkedStep, setCheckedStep] = useState(0);
  const [selectedBaselineYear, setSelectedBaselineYear] =
    useState(accountingYear);

  // selectedBaselineYear's initial value is only ever read once, at mount --
  // but accountingYear can still be today's placeholder guess at that point
  // (derivedStartDate hasn't resolved yet; see BaselineSlider's own
  // emissionsLoading comment). Resync it here until the user actually picks
  // a year themselves via the Select below.
  const hasPickedBaselineYearRef = useRef(false);
  useEffect(() => {
    if (hasPickedBaselineYearRef.current) {
      return undefined;
    }

    setSelectedBaselineYear(accountingYear);
    return undefined;
  }, [accountingYear]);

  const effectiveYear = isBaselineInput ? selectedBaselineYear : accountingYear;
  const accountingDate = new Date(effectiveYear, 11, 31);

  const hasCompletedYear = lastCompletedYear >= startYear;

  const baselineYearOptions =
    isBaselineInput && hasCompletedYear
      ? buildYearsInRange(startYear, lastCompletedYear).map((year) => ({
          label: `${year}`,
          value: year
        }))
      : [];

  const preBaselineYears = isBaselineInput
    ? buildYearsInRange(startYear, selectedBaselineYear).filter(
        (year) => year !== selectedBaselineYear
      )
    : [];

  const disclaimers = [
    `All of my emissions are complete and accurate through ${effectiveYear}.`,
    `All of the information for my offices is complete and correct for ${effectiveYear}.`,
    `All of my employees and their commute schedules are complete and accurate for ${effectiveYear}.`
  ];

  const { isBlocked, blockers = [] } =
    findAccountingBlockingEmissions(accountingDate);

  const allChecked = checkedStep === disclaimers.length;

  const saveAccountingYear = async (year, isBaseline) => {
    const recurringEmissionsThisYear = filterRecurringEmissionsForYear(
      [
        ...monthlyCommuteEmissionsTons,
        ...monthlyUtilitiesEmissionsTons,
        ...separateUtilitiesOfficesDefaultEmissions
      ],
      year
    );

    if (recurringEmissionsThisYear.length) {
      const { writtenIds = [] } = await createTransactions(
        recurringEmissionsThisYear.map((emission) => ({
          ...emission,
          dateCreated: new Date(),
          source: "recurring-closed",
          archived: false
        }))
      );

      if (writtenIds.length !== recurringEmissionsThisYear.length) {
        throw new Error(
          `Closing ${year} failed to materialize ${
            recurringEmissionsThisYear.length - writtenIds.length
          } recurring emission(s) -- some were flagged as duplicates and were not saved. Please try again or contact support.`
        );
      }
    }

    const monthsArray = [...new Array(12)].map((_, idx) => {
      const monthStartDate = dayjs(new Date(year, idx, 1));
      const daysInMonth = dayjs(monthStartDate).daysInMonth();
      return {
        startDate: monthStartDate,
        endDate: dayjs(new Date(year, idx, daysInMonth))
      };
    });

    const filterToMonth = (emissions, { startDate, endDate }) =>
      emissions.filter((emission) =>
        filterByDateRange({
          filterStartDate: startDate,
          filterEndDate: endDate,
          ...emission
        })
      );

    const monthlySubcategoryEmissionsTons = monthsArray.map(
      ({ startDate, endDate }) => {
        const emissionsThisMonth = filterToMonth(allEmissions, {
          startDate,
          endDate
        });

        const uniqueEmissionSubcategories = [
          ...new Set(emissionsThisMonth.map(({ subcategory }) => subcategory))
        ];

        return Object.fromEntries(
          uniqueEmissionSubcategories.map((subcategory) => {
            const subcategoryEmissions = emissionsThisMonth.filter(
              (emission) => emission.subcategory === subcategory
            );
            const subcategoryTonsCo2e = sumTonsCo2e(subcategoryEmissions);
            return [subcategory, subcategoryTonsCo2e];
          })
        );
      }
    );

    // Location-based electricity, RECs, and kWh, frozen alongside the
    // Market-based subcategory tons -- see accountingElectricitySnapshot.js.
    // Usage sources are the same three arrays the report's facility
    // electricity table reads, so a locked year's Scope 2 page and that
    // table agree.
    const electricityUsageSources = [
      ...utilitiesTransactions,
      ...monthlyUtilitiesEmissionsTons,
      ...separateUtilitiesOfficesDefaultEmissions
    ];
    const electricitySnapshotFields = buildElectricitySnapshotFields(
      monthsArray.map((month) =>
        buildElectricityMonthSnapshot({
          marketEmissions: filterToMonth(allEmissions, month),
          usageSources: filterToMonth(electricityUsageSources, month)
        })
      )
    );

    await newCollectionDoc("accounting", {
      date: new Date(year, 11, 31),
      baseline: isBaseline,
      monthlySubcategoryEmissionsTons,
      ...electricitySnapshotFields
    });

    return recurringEmissionsThisYear.length > 0;
  };

  const onSave = async () => {
    setSaveDataLoading(true);

    const yearsToClose = isBaselineInput
      ? buildYearsInRange(startYear, selectedBaselineYear)
      : [accountingYear];

    // The baseline year's doc must be written before any pre-baseline
    // year's, not in parallel with them. emissions.js derives which years
    // are "closed" from whether a baseline year exists at all
    // (useAccountingData -> findYearsBaselinePledgePercentages returns none
    // without one), while Report.js reads accounting docs directly with no
    // baseline check. Writing pre-baseline years concurrently with the
    // baseline year risks locking those years with no baseline anywhere if
    // only the baseline write fails after the others already succeeded --
    // Report.js would then treat that year as closed while emissions.js
    // still treats it as open but unconverted, silently dropping its
    // Location-based kWh/RECs from the new usage summary.
    const baselineYear = isBaselineInput ? selectedBaselineYear : accountingYear;
    const preBaselineYearsToClose = yearsToClose.filter(
      (year) => year !== baselineYear
    );

    try {
      const baselineRecurringCreated = await saveAccountingYear(
        baselineYear,
        isBaselineInput
      );
      const preBaselineRecurringCreatedFlags = await Promise.all(
        preBaselineYearsToClose.map((year) => saveAccountingYear(year, false))
      );
      const recurringCreatedFlags = [
        baselineRecurringCreated,
        ...preBaselineRecurringCreatedFlags
      ];

      if (recurringCreatedFlags.some(Boolean)) {
        await updateAccountData({ hasUnaggregatedTransactions: true });
      }

      analyticsTrack(isBaselineInput ? "Baseline Set" : "Closed Acct Year", {
        year: effectiveYear
      });

      if (
        (!isBaselineInput && unClosedYears.length === 1) ||
        (isBaselineInput && dayjs().year() - selectedBaselineYear > 0)
      ) {
        analyticsTrack("Silver Unlock");
      }
      return setOpen(false);
    } catch (error) {
      // Leave the slider open so the user can see what happened and retry --
      // closing it here would read as a successful close that silently
      // dropped emissions.
      return activateSnackbar({ message: error.message, alert: "error" });
    } finally {
      setSaveDataLoading(false);
    }
  };

  const isProduction = process.env.REACT_APP_ENVIRONMENT === "production";

  if (dataLoading) {
    return <AnimatedLogo />;
  }

  // The baseline flow's whole premise is deriving startDate from real data
  // instead of guessing it -- if persisting that derived value fails (a
  // transient write error), letting the flow continue anyway would let
  // someone finish setting a baseline with no startDate ever saved, and
  // baselineDate then permanently disables the manual start-year editor
  // (EditStartDateButton), leaving the account stuck with no way to set one.
  if (startDateDerivationFailed) {
    return (
      <CollapsibleAlert
        severity="error"
        title="We couldn't save your accounting start date"
        subtitle="Please close this and try again. If the problem continues, contact support."
      />
    );
  }

  // Recurring/default-utilities projections don't count as a real signal to
  // derive a start date from (see findEarliestEmissionDate's own comment)
  // -- so an account whose only full-year data is projected, with no real
  // transaction yet, hits this same !hasCompletedYear branch but for a
  // different reason than genuinely not having a year of history yet.
  // Telling them to wait for "a full year of data" is confusing when their
  // dashboard already shows one; point them at what actually unblocks this
  // instead.
  if (isBaselineInput && !hasCompletedYear && hasNoRealStartDate) {
    return (
      <CollapsibleAlert
        severity="info"
        title="We can't confirm a real start date yet"
        subtitle="Projected commute and utility estimates aren't enough on their own -- add your first real transaction, or set your accounting start year manually in Settings, to set a baseline."
      />
    );
  }

  if (isBaselineInput && !hasCompletedYear) {
    return (
      <CollapsibleAlert
        severity="info"
        title="You need a full year of data before setting a baseline"
        subtitle="You can set your baseline once you've measured a complete calendar year of emissions."
      />
    );
  }

  const alertTitle = `Resolve these items before you can ${actionText}`;
  const alertSubtitle = (
    <List dense disablePadding>
      {blockers.map((blocker, idx) => (
        <ListItem
          key={`accounting-blocker-${idx}`}
          disableGutters
          sx={{ paddingTop: 0, paddingBottom: 0 }}
        >
          <ListItemText
            primary={formatBlockerMessage(blocker)}
            primaryTypographyProps={{ variant: "caption" }}
          />
        </ListItem>
      ))}
    </List>
  );

  if (isBlocked && isProduction) {
    return (
      <CollapsibleAlert
        title={alertTitle}
        subtitle={alertSubtitle}
        severity="warning"
      />
    );
  }

  return (
    <Grid container spacing={2} direction="column">
      {isBlocked && (
        <Grid item>
          <CollapsibleAlert
            severity="warning"
            title={alertTitle}
            subtitle={alertSubtitle}
          />
        </Grid>
      )}
      {isBaselineInput && (
        <Grid item>
          <Select
            label="Baseline year"
            options={baselineYearOptions}
            value={selectedBaselineYear}
            editValue={(value) => {
              hasPickedBaselineYearRef.current = true;
              setSelectedBaselineYear(Number(value));
            }}
            size="small"
          />
        </Grid>
      )}
      {!!preBaselineYears.length && (
        <Grid item>
          <CollapsibleAlert
            severity="info"
            title={`${preBaselineYears.join(", ")} will be closed as pre-baseline year${
              preBaselineYears.length > 1 ? "s" : ""
            }`}
            subtitle="These earlier years will be locked and tracked, but won't count toward your baseline comparison."
          />
        </Grid>
      )}
      <Grid item>
        <Typography variant="body1">
          Please confirm the following information to {actionText} for the year{" "}
          {effectiveYear}
        </Typography>
      </Grid>
      {disclaimers.map((disclaimer, idx) => (
        <AccountingChecklistStep
          key={`accounting-checklist-step-${idx}`}
          title={disclaimer}
          isChecked={idx < checkedStep}
          disabled={checkedStep > idx || idx > checkedStep}
          onCheck={() => setCheckedStep((currentStep) => currentStep + 1)}
        />
      ))}
      {allChecked && (
        <Grid
          item
          container
          spacing={2}
          justifyContent="space-between"
          wrap="nowrap"
        >
          <Grid item>
            <TextField
              label="Confirmation"
              helperText={`Please confirm that you understand by typing "I understand"`}
              value={confirmationText}
              setValue={setConfirmationText}
            />
          </Grid>
          <Grid item>
            <LoadingButton
              isLoading={saveDataLoading}
              label={actionText}
              variant="contained"
              color="primary"
              disabled={confirmationText.toLowerCase() !== "i understand"}
              onClick={onSave}
              size="small"
            />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

const LeftDisclaimerCopy = () => {
  const { palette } = useTheme();

  const sections = [
    {
      title:
        "Closing a year finalizes your emissions data and sets a baseline for future comparison.",
      subtitle: "A closed year:",
      bullets: [
        "Locks emissions data",
        "Enables year-over-year tracking",
        "Supports consistent reporting"
      ]
    },
    {
      title: "Before you Close:",
      subtitle:
        "Make sure your emissions data is as <strong>complete and accurate as possible.</strong> This includes:",
      bullets: [
        "All known activity data entered",
        "Emissions categorizations reviewed and finalized",
        "Any outstanding gaps addressed or intentionally estimated",
        "Once a year is closed, <strong>it cannot be edited</strong>"
      ]
    },
    {
      title: "Best Practice",
      subtitle:
        "Only close your carbon accounting year when you're confident the data represents your best understanding of that year's emissions. This ensures your reporting, targets, and progress tracking are built on a solid foundation."
    }
  ];

  return (
    <Grid direction="column" spacing={4} container>
      <Grid item>
        <Typography variant="h3">
          What does it mean to close your account?
        </Typography>
      </Grid>
      {sections.map(({ title, subtitle, bullets }, idx) => (
        <Grid item key={`section-${idx}`}>
          <Typography variant="h5" color="textPrimary">
            {title}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {parse(subtitle)}
          </Typography>
          {bullets && (
            <List sx={{ listStyleType: "disc", pl: 2 }}>
              {bullets.map((bullet, bulletIdx) => (
                <ListItem
                  key={`bullet-${bulletIdx}`}
                  sx={{
                    display: "list-item",
                    p: 0,
                    "&::marker": { color: palette.text.secondary }
                  }}
                >
                  <ListItemText
                    primary={parse(bullet)}
                    sx={{ color: palette.text.secondary }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Grid>
      ))}
    </Grid>
  );
};

const AccountingSlideLayout = ({ setOpen, type, ...otherProps }) => {
  const { palette } = useTheme();

  const isBaselineInput = type === "baseline";
  const actionText = isBaselineInput
    ? "set your baseline"
    : "close your accounting";

  return (
    <SlideLayout
      isSlideOpen
      setIsSlideOpen={setOpen}
      header={
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Avatar style={{ backgroundColor: palette.secondary.main }}>
              <FontAwesomeIcon
                icon={isBaselineInput ? faChartLineUpDown : faAbacus}
                style={{ color: "white" }}
                size="lg"
              />
            </Avatar>
          </Grid>
          <Grid item>
            <Typography variant="h4">{ucFirstLetters(actionText)}</Typography>
          </Grid>
        </Grid>
      }
      content={
        <AccountingSlideLayoutContent
          isBaselineInput={isBaselineInput}
          actionText={actionText}
          setOpen={setOpen}
          {...otherProps}
        />
      }
      leftContent={
        <ThemeProvider theme={mergeDarkTheme()}>
          <LeftDisclaimerCopy />
        </ThemeProvider>
      }
    />
  );
};

const BaselineSlider = ({ setOpen }) => {
  const [{ startDate }, accountDataLoading] = useAccountData();
  const { updateAccountData } = useCachedFirebaseCrud();
  const { mutate } = useSWRConfig();
  // emissionsLoading matters here specifically because derivedStartDate
  // depends on allEmissions -- without waiting on it, this slider can open
  // (isBronzeEarned/baselineDate don't gate on it) while allEmissions is
  // still empty, derive a startYear from nothing, and initialize this
  // whole flow's selectedBaselineYear state from that wrong, temporary
  // value; that state never re-syncs once the real emissions (and the real
  // derived year) arrive.
  const { allEmissions, emissionsLoading } = useEmissionsContext();
  const { activateSnackbar } = usePlatformLayoutContext();

  const derivedStartDate = deriveStartDateFromEmissions(allEmissions);

  // useCachedFirebaseCrud's updateAccountData mutates the local SWR cache
  // optimistically (helpers/firebase.js) BEFORE the Firestore write it
  // kicks off is confirmed -- so startDate here can flip truthy the moment
  // our own persist attempt starts, well before it actually succeeds or
  // fails. Tracking the outcome via startDate (or via a cleanup keyed to
  // it) would treat that optimistic flip as confirmation and could let a
  // still-failing write's rejection get silently dropped once it lands.
  // hasAttemptedPersistRef (not state, so it can't itself trigger a
  // re-render/re-run) guards against calling updateAccountData more than
  // once; persistOutcome is set only by this specific call's own
  // .then/.catch, independent of any later re-render.
  const hasAttemptedPersistRef = useRef(false);
  const [persistOutcome, setPersistOutcome] = useState(() =>
    startDate ? "not-needed" : "pending"
  );

  useEffect(() => {
    if (startDate && !hasAttemptedPersistRef.current) {
      // Real from the start -- nothing was ever derived or persisted here.
      setPersistOutcome("not-needed");
      return undefined;
    }

    if (startDate || hasAttemptedPersistRef.current) {
      // Either our own persist already landed or is still in flight -- its
      // own .then/.catch below is the single source of truth for the
      // outcome, not a re-render of this effect.
      return undefined;
    }

    if (!derivedStartDate) {
      // Nothing real to derive from yet -- not blocked, just genuinely
      // nothing to persist (re-evaluated below if allEmissions changes,
      // e.g. a live listener delivers the company's first real
      // transaction after this slider is already open).
      setPersistOutcome("not-needed");
      return undefined;
    }

    hasAttemptedPersistRef.current = true;
    // A later-arriving derivedStartDate (allEmissions updated after this
    // effect already set "not-needed") must re-block the flow before
    // starting the write -- otherwise the checklist stays usable, already
    // unblocked from the earlier "not-needed" state, while this new write
    // is still outstanding.
    setPersistOutcome("pending");

    updateAccountData({ startDate: derivedStartDate })
      .then(() => setPersistOutcome("succeeded"))
      .catch(() => {
        setPersistOutcome("failed");
        activateSnackbar({
          message:
            "We couldn't save your accounting start date. Please try again.",
          alert: "error"
        });
        // updateAccountData's optimistic cache mutation (helpers/firebase.js)
        // is never rolled back on a rejected write -- without this, a
        // remount after "close and try again" would read that stale,
        // never-persisted startDate back out of the SWR cache, treat it as
        // already real (skipping this whole effect), and let baseline
        // creation proceed with nothing actually saved in Firestore. A
        // revalidating mutate() isn't enough on its own: it's a network
        // round-trip, so a fast enough remount can still read the stale
        // value before it resolves (or see it never correct at all, if the
        // revalidation itself fails). Reverting the cache synchronously,
        // with no network dependency, closes that gap immediately -- we
        // already know startDate was falsy before this attempt started, so
        // reverting to null is always correct here.
        mutate(
          "accountData",
          (companyData) => ({ ...companyData, startDate: null }),
          false
        );
      });

    return undefined;
  }, [
    startDate,
    derivedStartDate,
    updateAccountData,
    activateSnackbar,
    mutate
  ]);

  // Block the flow on the same write this whole derive-from-real-data
  // premise depends on -- otherwise a slow or dropped persist still lets
  // someone finish setting a baseline with no startDate ever saved.
  const isPersistingDerivedStartDate = persistOutcome === "pending";
  const startDateDerivationFailed = persistOutcome === "failed";

  // Distinguishes "genuinely no real signal yet" (nothing to show but
  // recurring/default-utilities projections, if even that) from "has a
  // real or derived date, just hasn't reached a full year" -- both trip
  // the same !hasCompletedYear check below, but only the first one is
  // wrongly described by "you need a full year of data" when the
  // dashboard already shows a full year of (unverified) emissions.
  const hasNoRealStartDate = !startDate && !derivedStartDate;

  // `undefined`, never `null`, if there's truly nothing to derive from --
  // dayjs(undefined) resolves to "now" (a valid, if unhelpful, year), where
  // dayjs(null) is Invalid Date and NaN's its way into a RangeError further
  // down (buildYearsInRange's Array(NaN)) before hasCompletedYear's own
  // "no complete year yet" check ever gets a chance to short-circuit it.
  const startYear = dayjs(startDate || derivedStartDate || undefined).year();
  const lastCompletedYear = dayjs().year() - 1;

  return (
    <AccountingSlideLayout
      setOpen={setOpen}
      type="baseline"
      dataLoading={
        accountDataLoading || emissionsLoading || isPersistingDerivedStartDate
      }
      startDateDerivationFailed={startDateDerivationFailed}
      hasNoRealStartDate={hasNoRealStartDate}
      accountingYear={startYear}
      startYear={startYear}
      lastCompletedYear={lastCompletedYear}
    />
  );
};

const CloseAccountingSlider = ({ setOpen }) => {
  const [{ unClosedYears = [{}] }] = useAccountingData();

  // Bare dayjs(), deliberately -- do NOT "fix" this to dayjs.utc(). This
  // `date` is unClosedYears[].date from @aclymatepackages/accounting's
  // buildFutureAccountingYearsArray, which increments whole years off
  // mostRecentAccountingDate -- itself always a real `accounting` doc's
  // Dec-31-UTC-midnight `date` field (this code path only runs once
  // baselineDate exists, which requires at least one such doc). A 1-day
  // UTC-offset shift off a Dec-31 anchor never crosses a year boundary, so
  // this read is safe even without .utc(). See CloseAccountingButton PR
  // description for the full trace.
  const [{ date }] = unClosedYears;
  const year = dayjs(date).year();

  return (
    <AccountingSlideLayout
      setOpen={setOpen}
      type="accounting"
      accountingYear={year}
    />
  );
};

const CloseAccountingButton = ({
  type,
  color = "primary",
  disabled,
  accountingYear,
  size = "medium",
  ButtonComponent
}) => {
  const { isSimulation } = usePlatformLayoutContext();
  const { isAdmin } = usePermissions();

  const [accountingActionModalOpen, setAccountingActionModalOpen] =
    useState(false);

  const dialogBoxTypes = {
    baseline: <BaselineSlider setOpen={setAccountingActionModalOpen} />,
    accounting: (
      <CloseAccountingSlider
        setOpen={setAccountingActionModalOpen}
        accountingYear={accountingYear}
      />
    )
  };

  const inputBox = dialogBoxTypes[type];

  const buttonProps = {
    onClick: () => setAccountingActionModalOpen(true),
    disabled: disabled || isSimulation || !isAdmin
  };

  const button = ButtonComponent ? (
    <ButtonComponent {...buttonProps} />
  ) : (
    <Button
      key="close-accounting-button"
      variant="contained"
      color={color}
      size={size}
      {...buttonProps}
    >
      {type === "baseline" ? "set your baseline" : "close your accounting"}
    </Button>
  );

  return (
    <>
      {accountingActionModalOpen && inputBox}
      {button}
    </>
  );
};
export default CloseAccountingButton;
